import { useEffect, useRef } from 'react';

import type { PlatformName } from '../api/types.js';
import { PLATFORM_HELP } from './platform-help.js';
import { PlatformIcon } from './PlatformIcon.js';

/**
 * Setup instructions for one platform (ARCHITECTURE.md §65).
 *
 * Built on native `<dialog>` for the same reason the Discord channel picker is:
 * focus trapping, Escape, page inertness and correct semantics come from the
 * platform rather than from JavaScript somebody has to maintain.
 *
 * The content is deliberately specific. Generic help ("configure your
 * credentials") wastes the reader's time; every step here names a screen, a
 * value to copy, or the mistake that most often costs an hour.
 */

export type PlatformHelpDialogProps = {
  platform: PlatformName | null;
  displayName: string;
  onClose: () => void;
};

export function PlatformHelpDialog({
  platform,
  displayName,
  onClose,
}: PlatformHelpDialogProps): React.JSX.Element | null {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const help = platform === null ? undefined : PLATFORM_HELP[platform];

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog === null) return;

    // showModal(), not the `open` attribute: only the former makes the rest of
    // the page inert and traps focus.
    if (platform !== null && !dialog.open) dialog.showModal();
    else if (platform === null && dialog.open) dialog.close();
  }, [platform]);

  if (platform === null || help === undefined) return null;

  return (
    <dialog
      ref={dialogRef}
      className="dialog dialog--help"
      aria-labelledby="platform-help-title"
      onClose={onClose}
      onCancel={onClose}
    >
      <div className="dialog__header">
        <h2 className="dialog__title platform-title" id="platform-help-title">
          <PlatformIcon platform={platform} displayName={displayName} />
          Set up {displayName}
        </h2>
        <button type="button" className="btn btn--ghost" onClick={onClose} aria-label="Close">
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
            <path
              d="M6 6l12 12M18 6L6 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>

      <div className="dialog__body">
        <p className="help__summary">{help.summary}</p>

        {help.limitation === undefined ? null : (
          /*
           * Stated before the steps, not after. Someone about to spend twenty
           * minutes creating an application deserves to know what it will and
           * will not get them BEFORE they start.
           */
          <p className="callout callout--limit">
            <strong>Worth knowing first:</strong> {help.limitation}
          </p>
        )}

        <ol className="help__steps">
          {help.steps.map((step) => (
            <li key={step.text}>
              <span>{step.text}</span>
              {step.code === undefined ? null : <code className="help__code">{step.code}</code>}
            </li>
          ))}
        </ol>

        {/*
         * Every hazard, not the first one.
         *
         * Discord has two of the same shape — the Message Content Intent and
         * channel permissions — each producing a connection that reports
         * healthy while delivering nothing. Showing one would mean choosing
         * which silent failure an operator finds out about the hard way, and
         * the second one cost a real afternoon before it was written down.
         */}
        {(help.gotchas ?? []).map((gotcha, index) => (
          <p className="callout callout--gotcha" key={gotcha}>
            <strong>
              {(help.gotchas ?? []).length === 1
                ? 'Most common mistake:'
                : `Watch out (${String(index + 1)} of ${String((help.gotchas ?? []).length)}):`}
            </strong>{' '}
            {gotcha}
          </p>
        ))}
      </div>

      <div className="dialog__footer">
        <a className="btn" href={help.portalUrl} target="_blank" rel="noreferrer noopener">
          Open {help.portalLabel}
          {/* An external-link mark, announced in text rather than by icon alone. */}
          <span className="visually-hidden">(opens in a new tab)</span>
          <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false">
            <path
              d="M14 5h5v5M19 5l-8 8M18 14v4a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h4"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </a>
        <button type="button" className="btn btn--primary" onClick={onClose}>
          Done
        </button>
      </div>
    </dialog>
  );
}
